import { isValidEmail } from "pages/StartLead/validation";

export const EMAIL_EDIT_ERROR = "Enter a full email address";

/** The email the booking request sends, held until the booking succeeds:
 * the server saves it to the contact then, not before. */
export type EmailCorrection = string | null;

/**
 * Where the confirmation email stands on the scheduler. It is absent until
 * a booking comes back refused because the email can't be delivered to,
 * then stays for the life of the scheduler.
 */
export type ConfirmationEmailState =
  | { kind: "hidden" }
  | { kind: "refused"; email: string }
  | { kind: "corrected"; email: string }
  | { kind: "ask" };

export const CONFIRMATION_EMAIL_HIDDEN: ConfirmationEmailState = {
  kind: "hidden",
};

/** The state once a booking is refused for its email: `email` is the
 * address the server tried, null when it had none to try. */
export function emailStateAfterRefusal(
  email: string | null,
): ConfirmationEmailState {
  if (email === null) return { kind: "ask" };
  return { kind: "refused", email };
}

/** The state once the family has applied an address. */
export function emailStateAfterCorrection(
  email: string,
): ConfirmationEmailState {
  return { kind: "corrected", email };
}

/** What the confirmation email element says. `email` renders in bold
 * between `lead` and `trail`. */
export interface ConfirmationEmailCopy {
  tone: "alert" | "neutral";
  lead: string;
  email: string | null;
  trail: string;
  /** The text button that opens the editor. */
  prompt: string;
  /** The editor is open when the element appears: the family has to supply
   * an address before a booking can succeed. */
  opensEditing: boolean;
}

/** The element's copy for a state, or null while it is hidden. */
export function confirmationEmailCopy(
  state: ConfirmationEmailState,
): ConfirmationEmailCopy | null {
  switch (state.kind) {
    case "hidden":
      return null;
    case "refused":
      return {
        tone: "alert",
        lead: "We couldn’t book your call because we can’t email ",
        email: state.email,
        trail: ". Please correct it and try again.",
        prompt: "Fix it",
        opensEditing: false,
      };
    case "corrected":
      return {
        tone: "neutral",
        lead: "Confirmation goes to ",
        email: state.email,
        trail: ".",
        prompt: "Not right? Fix it",
        opensEditing: false,
      };
    case "ask":
      return {
        tone: "neutral",
        lead: "Where should we send your confirmation?",
        email: null,
        trail: "",
        prompt: "Add an email",
        opensEditing: true,
      };
  }
}

/** Whether a booking can't succeed until the family supplies an address. */
export function emailBlocksBooking(state: ConfirmationEmailState): boolean {
  return state.kind === "refused" || state.kind === "ask";
}

/** What the editor opens with: the refused or corrected address, else empty. */
export function emailEditorStart(state: ConfirmationEmailState): string {
  if (state.kind === "refused" || state.kind === "corrected") {
    return state.email;
  }
  return "";
}

/** Null when the draft can be applied, otherwise what to tell the family. */
export function emailEditError(draft: string): string | null {
  return isValidEmail(draft) ? null : EMAIL_EDIT_ERROR;
}

/** The booking request's body. `email` rides along only when the family
 * corrected it; an empty one would be rejected. */
export function bookingRequestBody(
  start: string,
  timezone: string,
  correction: EmailCorrection,
): { start: string; timezone: string; email?: string } {
  if (correction === null) return { start, timezone };
  return { start, timezone, email: correction };
}
